import React, {useContext, useState, useEffect} from "react"

import http from "./../services/httpService";
import httpNoAuth from "./../services/httpNoAuthService";
import config from "../constants";
import {getPermissionsAndNavigation, checkAuthStatus} from "../api/accounts";

const apiEndpoint = config.API_URL + "/accounts/";

const AuthContext = React.createContext()

export function useAuth() {
    return useContext(AuthContext)
}

export function AuthProvider({children}) {

    const [currentUser, setCurrentUser] = useState()
    const [currentUserId, setCurrentUserId] = useState()
    const [loading, setLoading] = useState(true)
    const [permissions, setPermissions] = useState([])
    const [navigation, setNavigation] = useState([])
    const [currentMainNavItem, setCurrentMainNavItem] = useState()

    async function login(email, password) {
        const response = await httpNoAuth.post(apiEndpoint + "token/", {"username": email, "password": password});
        // Backend now sets httpOnly cookies and returns { "message": "Login successful" }
        // Get user info from backend since we can't decode JWT anymore
        const userInfo = await checkAuthStatus();
        setCurrentUser(userInfo)
        setCurrentUserId(userInfo.user_id)
        // Keep cartList functionality
        localStorage.setItem('cartList', []);
    }

    function logout() {
        setLoading(true)

        // remove wonderchat iframe
        let script = document.querySelector("#wonderchat-wrapper");
        if (script) script.parentNode?.removeChild(script);

        // Backend now reads refresh token from cookies and clears them
        http.post(apiEndpoint + "token/logout/");

        // Clear user state
        setCurrentUser(null);
        setCurrentUserId(null);

        // Keep cartList functionality
        localStorage.removeItem('cartList');
        setLoading(false)
        return
    }

    async function loginAs(userId) {
        const response = await http.post(config.API_URL + `/users_for_admin/${userId}/token/`);
        // Backend now sets httpOnly cookies and returns { "message": "Login successful" }
        // Get user info from backend since we can't decode JWT anymore
        const userInfo = await checkAuthStatus();
        setCurrentUser(userInfo)
        setCurrentUserId(userInfo.user_id)
        // Keep cartList functionality
        localStorage.setItem('cartList', []);
    }

    useEffect(() => {
        // Check authentication status on app load
        async function checkAuth() {
            // Skip auth check for public routes that don't require authentication
            const publicRoutes = [
                '/login',
                '/forgot-password',
                '/reset-password',
                '/public/',
                '/summer-school/registration/new',
                '/summer-school/registration/retrieve'
            ];

            // Special handling for summer school registration routes with secrets
            const summerSchoolSecretRoutePattern = /^\/summer-school\/registration\/\d+\/[^\/]+\/(edit|confirm)$/;

            const currentPath = window.location.pathname;
            const isPublicRoute = publicRoutes.some(route =>
                currentPath === route || currentPath.startsWith(route)
            ) || summerSchoolSecretRoutePattern.test(currentPath);

            if (isPublicRoute) {
                // For public routes, just set loading to false without checking auth
                setCurrentUser(null);
                setCurrentUserId(null);
                setLoading(false);
                return;
            }

            try {
                const userInfo = await checkAuthStatus();
                setCurrentUser(userInfo);
                setCurrentUserId(userInfo.id);
            } catch (e) {
                // User is not authenticated or session expired
                setCurrentUser(null);
                setCurrentUserId(null);
            }
            setLoading(false);
        }

        checkAuth();
    }, [])

    useEffect(() => {
        if (currentUser) {
            getPermissionsAndNavigation().then((r) => {
                setPermissions(r.permissions)
                setNavigation(r.navigation)
                if (!currentMainNavItem) {
                    const path = window.location.pathname
                    const navItem = r.navigation.main_navigation.find((item) => item.path === path || item.sub_items.find((subItem) => path.startsWith(subItem.path)))
                    setCurrentMainNavItem(navItem)
                }
            }).catch(() => {})
        }
    }, [currentUser])

    const value = {
        currentUser,
        currentUserId,
        permissions,
        navigation,
        currentMainNavItem,
        setCurrentMainNavItem,
        login,
        logout,
        loginAs
    }

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    )
}
